"use client";

import { useCallback, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/nextjs";
import { useReactFlow } from "@xyflow/react";
import { nanoid } from "nanoid";
import { toast } from "sonner";

import { useProject } from "@/providers/project";
import { useNodeOperations } from "@/providers/node-operations";
import { uploadFile } from "@/lib/upload";
import { handleError } from "@/lib/error/handle";
import { splitImageUrlToPngGrid } from "@flowmuse/models/shared/image-size";
import { env } from "@/lib/env";
import { usdToCredits } from "@/lib/credits/estimate";
import { useCreditRate } from "@/hooks/use-credit-rate";
import { usePreviewCanvas } from "@/hooks/use-preview-canvas";

export type ImageGridSplitHookParams = {
  nodeId: string;
  imageUrl?: string;
  /**
   * 当节点正在上传/生成/处理中时外部可禁用分割
   */
  disabled?: boolean;
  /**
   * i18n 翻译函数
   */
  t: (key: string, params?: Record<string, any>) => string;
};

export type GridSplitPreset = "2x2" | "3x3" | "4x4" | "5x5" | "custom";

/**
 * 图像网格分割：仅提供状态与执行逻辑，UI 由调用方渲染
 * （顶部工具栏以 Popover 形式展示，与画质增强等功能保持同一样式）。
 */
export function useImageGridSplit(params: ImageGridSplitHookParams) {
  const { nodeId, imageUrl, disabled, t } = params;
  const project = useProject();
  const { addNode } = useNodeOperations();
  const { getNode, setNodes } = useReactFlow();
  const { getToken } = useAuth();
  const { user } = useUser();
  const { data: creditRate } = useCreditRate();
  const creditValueUsd = creditRate?.creditValueUsd ?? 0.01;
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);

  const [preset, setPreset] = useState<GridSplitPreset>("2x2");
  const [customRowsText, setCustomRowsText] = useState<string>("2");
  const [customColsText, setCustomColsText] = useState<string>("2");
  const [isSplitting, setIsSplitting] = useState(false);

  const MAX_PIECES = 25;
  const getSplitUsdCost = useCallback((rows: number, cols: number) => {
    const pieces = Math.max(0, Math.floor(rows) * Math.floor(cols));
    if (pieces <= 9) return 0;
    // 计费：5/(1000/块数)*1.5 USD = 0.0075 * 块数
    return 0.0075 * pieces;
  }, []);

  const getSplitCredits = useCallback((rows: number, cols: number) => {
    const usdCost = getSplitUsdCost(rows, cols);
    if (usdCost <= 0) return 0;
    return usdToCredits(usdCost, creditValueUsd);
  }, [creditValueUsd, getSplitUsdCost]);

  const parsePositiveInt = (raw: string): number | undefined => {
    const n = Number(String(raw).trim());
    if (!Number.isFinite(n)) return undefined;
    const i = Math.floor(n);
    if (i <= 0) return undefined;
    return i;
  };

  const customRows = useMemo(() => parsePositiveInt(customRowsText) ?? 0, [customRowsText]);
  const customCols = useMemo(() => parsePositiveInt(customColsText) ?? 0, [customColsText]);
  const customCredits = useMemo(() => (customRows > 0 && customCols > 0 ? getSplitCredits(customRows, customCols) : 0), [customRows, customCols, getSplitCredits]);
  const customValid = useMemo(() => {
    if (customRows <= 0 || customCols <= 0) return false;
    if (customRows > 10 || customCols > 10) return false;
    if (customRows * customCols > MAX_PIECES) return false;
    return true;
  }, [customRows, customCols]);

  /** 执行分割；成功返回 true（调用方据此关闭 Popover） */
  const run = useCallback(
    async (rows: number, cols: number): Promise<boolean> => {
      const projectId = project?.id;
      if (!imageUrl || !projectId || effectiveDisabled || isSplitting) return false;
      try {
        setIsSplitting(true);
        const token = await getToken({ template: "supabase" });
        if (!token) throw new Error(t("No Clerk token"));
        if (!user?.id) throw new Error(t("No Clerk user"));

        // 大网格需要扣积分（小网格免费）
        const neededCredits = getSplitCredits(rows, cols);
        if (neededCredits > 0) {
          const res = await fetch("/api/image/split", {
            method: "POST",
            headers: { "content-type": "application/json", "x-internal-secret": env.NEXT_PUBLIC_INTERNAL_API_SECRET },
            body: JSON.stringify({ projectId, nodeId, rows, cols }),
          });
          if (!res.ok) {
            let msg = "Insufficient credits";
            try {
              const j = (await res.json()) as any;
              msg = String(j?.error || msg);
            } catch {}
            if (res.status === 402 || /(insufficient)/i.test(msg)) {
              try { window.dispatchEvent(new Event("open_buy_credits")); } catch {}
            }
            throw new Error(msg);
          }
        }

        const { pieces } = await splitImageUrlToPngGrid({ url: imageUrl, rows, cols });
        if (!pieces.length) throw new Error("No pieces");

        const cur = getNode(nodeId) as any;
        const baseX = cur?.position?.x ?? 0;
        const baseY = cur?.position?.y ?? 0;
        const width = (cur?.measured?.width ?? 300) as number;
        const height = (cur?.measured?.height ?? 240) as number;
        // 分割模式：与原节点保持横向间距，但分割出来的节点彼此紧贴
        const outerGapX = 120;
        const gap = 0;
        const vGap = 0;

        const nowIso = new Date().toISOString();
        const newIds: string[] = [];

        for (let i = 0; i < pieces.length; i++) {
          const p = pieces[i]!;
          const filename = `split_${rows}x${cols}_${p.row + 1}-${p.col + 1}_${nanoid()}.png`;
          const file = new File([p.blob], filename, { type: p.type });
          const uploaded = await uploadFile(file, "files", { token, userId: user.id, filename });

          const position = {
            x: baseX + width + outerGapX + p.col * (width + gap),
            y: baseY + p.row * (height + vGap),
          };

          const newId = addNode("image", {
            position,
            selected: i === 0,
            data: {
              content: { url: uploaded.url, type: uploaded.type || p.type },
              sourceUrl: uploaded.url,
              size: `${p.width}x${p.height}`,
              updatedAt: nowIso,
            },
          });
          if (newId) newIds.push(String(newId));
        }

        if (newIds.length) {
          try {
            const keep = String(newIds[0]);
            setNodes((prev: any[]) =>
              prev.map((n: any) => ({ ...n, selected: String(n.id) === keep }))
            );
          } catch {}
        }

        toast.success(t("Split into {{count}} images", { count: pieces.length }));
        return true;
      } catch (error) {
        handleError(t("Failed to split image"), error);
        return false;
      } finally {
        setIsSplitting(false);
      }
    },
    [addNode, effectiveDisabled, getNode, getSplitCredits, getToken, imageUrl, isSplitting, nodeId, project?.id, setNodes, t, user?.id]
  );

  return {
    run,
    isSplitting,
    disabled: effectiveDisabled,
    preset,
    setPreset,
    customRowsText,
    setCustomRowsText,
    customColsText,
    setCustomColsText,
    customRows,
    customCols,
    customValid,
    customCredits,
    getSplitCredits,
  };
}
